import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "../common/LanguageSwitcher";
import { useTranslation } from "@/hooks/useTranslation";
import { 
  Drawer, 
  DrawerContent, 
  DrawerClose, 
  DrawerTrigger 
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const isMobile = useIsMobile();

  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const navItems = [
    { label: t("nav.home"), href: "/" },
    { label: t("nav.features"), href: "/features" },
    { label: t("nav.pricing"), href: "/pricing" },
    { label: t("nav.about"), href: "/about" },
    { label: t("nav.blog"), href: "/blog" },
    { label: t("nav.contact"), href: "/contact" },
  ];

  const isActive = (path: string) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 bg-white shadow-sm ${
        isScrolled ? "py-2" : "py-3"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="/HostCall-ai-logo-v3-transp.png"
              alt="HostCall.ai"
              width={147}
              height={40}
              loading="eager"
              decoding="async"
              className="h-8 md:h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? "text-brand-600 bg-brand-50"
                    : "text-gray-700 hover:text-brand-600 hover:bg-gray-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
            
            <div className="ml-4 pl-4 border-l border-gray-200">
              <LanguageSwitcher />
            </div>

            <a href="https://app.hostcall.ai" target="_blank" rel="noopener noreferrer" className="ml-6">
              <Button 
                variant="outline" 
                className="min-w-[80px] min-h-[44px] px-5 border-brand-500 text-brand-600 hover:bg-brand-50 hover:text-brand-700 font-medium"
              >
                {t("nav.login")}
              </Button>
            </a>

            <Link to="/demo" className="ml-4">
              <Button className="min-h-[44px] px-5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md shadow-orange-500/20 font-medium">
                {t("nav.demo")}
              </Button>
            </Link>
          </nav>

          {/* Mobile Navigation using Drawer */}
          <div className="flex items-center md:hidden">
            <div className="mr-4">
              <LanguageSwitcher />
            </div>
            
            <Drawer>
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="text-gray-700" aria-label="Open menu">
                  <Menu size={24} />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="pt-6">
                <div className="px-4 mb-4 flex justify-end">
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon" aria-label="Close menu">
                      <X size={24} />
                    </Button>
                  </DrawerClose>
                </div>
                <nav className="flex flex-col space-y-2 px-4 pb-8">
                  {navItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`px-3 py-3 rounded-md text-base font-medium ${
                        isActive(item.href)
                          ? "text-brand-600 bg-brand-50"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                  <a 
                    href="https://app.hostcall.ai" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="mt-4"
                  >
                    <Button 
                      variant="outline" 
                      className="w-full border-brand-500 text-brand-600 hover:bg-brand-50 font-medium"
                    >
                      {t("nav.login")}
                    </Button>
                  </a>
                  <Link to="/demo" className="mt-2">
                    <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md font-medium">
                      {t("nav.demo")}
                    </Button>
                  </Link>
                </nav>
              </DrawerContent>
            </Drawer>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
